/**
 * PropertySearchResults - Optimized property search results layout
 * 2-column desktop layout with listings and context panel
 * Mobile-first responsive design with hover-to-zoom map support
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { 
  Map, X, Loader2, AlertCircle, ChevronDown, 
  SlidersHorizontal, ArrowUpDown, Grid, List
} from 'lucide-react';
import PropertyResultCard from './PropertyResultCard';
import PropertyContextPanel from './PropertyContextPanel';
import CompareProperties from './CompareProperties';
import { useCountry } from '../context/CountryContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @param {string} unit - 'km' or 'mile'
 * @returns {number} Distance in specified unit
 */
const calculateDistance = (lat1, lon1, lat2, lon2, unit = 'km') => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  
  const R = unit === 'km' ? 6371 : 3959; // Earth's radius in km or miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  // Return with appropriate precision
  if (distance < 1) {
    return distance.toFixed(2);
  } else if (distance < 10) {
    return distance.toFixed(1);
  }
  return Math.round(distance);
};

export default function PropertySearchResults({
  listings,
  loading,
  error,
  totalCount,
  filters,
  sortBy,
  onSortChange,
  onFilterRemove,
  onClearFilters,
  searchCenter,
  hoverZoomSettings: propHoverZoomSettings
}) {
  const navigate = useNavigate();
  const { country } = useCountry();
  const [highlightedId, setHighlightedId] = useState(null);
  const [showMobileMap, setShowMobileMap] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const listingRefs = useRef({});
  
  // Distance unit based on country - Sri Lanka uses km, UK uses miles
  const distanceUnit = (country === 'UK' || country === 'GB') ? 'mile' : 'km';
  
  // Calculate distances for all listings when searchCenter is available
  const listingsWithDistance = useMemo(() => {
    if (!searchCenter || !searchCenter[0] || !searchCenter[1]) {
      return listings.map(listing => ({ ...listing, calculatedDistance: null }));
    }
    
    return listings.map(listing => {
      const lat = listing.latitude || listing.lat;
      const lon = listing.longitude || listing.lng || listing.lon;
      
      if (!lat || !lon) {
        return { ...listing, calculatedDistance: null };
      }
      
      const distance = calculateDistance(
        searchCenter[0], searchCenter[1],
        lat, lon,
        distanceUnit
      );
      
      return { ...listing, calculatedDistance: distance };
    });
  }, [listings, searchCenter, distanceUnit]);
  
  // Compare state
  const [compareIds, setCompareIds] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const MAX_COMPARE = 4;
  
  // Hover-to-zoom settings (fetch from admin if not provided as prop)
  const [hoverZoomSettings, setHoverZoomSettings] = useState(
    propHoverZoomSettings || { enabled: true, zoomLevel: 15, animationDuration: 500, enablePan: true }
  );
  
  // Fetch hover-to-zoom settings if not provided
  useEffect(() => {
    if (propHoverZoomSettings) {
      setHoverZoomSettings(propHoverZoomSettings);
      return;
    }
    
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/notifications/map-features/${country || 'LK'}`);
        if (response.data) {
          setHoverZoomSettings({
            enabled: response.data.enabled ?? true,
            zoomLevel: response.data.settings?.zoom_level ?? 15,
            animationDuration: response.data.settings?.animation_duration ?? 500,
            enablePan: response.data.settings?.enable_pan ?? true
          });
        }
      } catch (err) {
        console.error('Failed to fetch hover-to-zoom settings:', err);
      }
    };
    
    fetchSettings();
  }, [country, propHoverZoomSettings]);
  
  const handleCompareToggle = useCallback((listingId) => {
    setCompareIds(prev => {
      if (prev.includes(listingId)) {
        return prev.filter(id => id !== listingId);
      }
      if (prev.length >= MAX_COMPARE) {
        return prev;
      }
      return [...prev, listingId];
    });
  }, []);
  
  const handleMarkerClick = useCallback((listingId) => {
    setHighlightedId(listingId);
    // Scroll to listing
    if (listingRefs.current[listingId]) {
      listingRefs.current[listingId].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  }, []);

  // Build active filters for chips
  const activeFilters = [];
  if (filters?.minPrice || filters?.maxPrice) {
    const minLabel = filters.minPrice ? `£${filters.minPrice.toLocaleString()}` : 'No min';
    const maxLabel = filters.maxPrice ? `£${filters.maxPrice.toLocaleString()}` : 'No max';
    activeFilters.push({ key: 'price', label: `${minLabel} - ${maxLabel}`, value: 'price' });
  }
  if (filters?.bedrooms && filters.bedrooms !== 'any') {
    activeFilters.push({ key: 'bedrooms', label: `${filters.bedrooms}+ beds`, value: filters.bedrooms });
  }
  if (filters?.propertyType && filters.propertyType !== 'any') {
    activeFilters.push({ key: 'propertyType', label: filters.propertyType, value: filters.propertyType });
  }
  if (filters?.location) {
    activeFilters.push({ key: 'location', label: filters.location, value: filters.location });
  }

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      {/* Active Filter Chips */}
      {activeFilters.length > 0 && (
        <div className="bg-white border-b px-4 py-2 sticky top-[72px] z-30">
          <div className="max-w-[1440px] mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {activeFilters.map((filter) => (
              <Badge 
                key={filter.key}
                variant="secondary" 
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                onClick={() => onFilterRemove?.(filter.key)}
              >
                {filter.label}
                <X className="w-3 h-3" />
              </Badge>
            ))}
            {activeFilters.length > 1 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-500 whitespace-nowrap"
                onClick={onClearFilters}
              >
                Clear all
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Results Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm">
              <strong className="text-gray-900">{totalCount.toLocaleString()}</strong>
              <span className="text-gray-500 ml-1">results</span>
            </span>
            
            {/* Sort dropdown */}
            <div className="hidden sm:flex items-center gap-2 text-sm">
              <span className="text-gray-500">Sort:</span>
              <select 
                value={sortBy}
                onChange={(e) => onSortChange?.(e.target.value)}
                className="bg-transparent font-medium cursor-pointer focus:outline-none"
              >
                <option value="recent">Newest</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="hidden sm:flex items-center border rounded-lg p-0.5">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Mobile map toggle */}
            <Button
              variant="outline"
              size="sm"
              className="lg:hidden"
              onClick={() => setShowMobileMap(true)}
            >
              <Map className="w-4 h-4 mr-1.5" />
              Map
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="max-w-[1440px] mx-auto px-4 py-6">
        <div className="flex gap-8 justify-center">
          {/* Listings Column */}
          <div className={`w-full ${viewMode === 'list' ? 'max-w-[760px]' : 'lg:max-w-[760px]'}`}>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                <p className="text-gray-700 font-medium">{error}</p>
                <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
                  Try Again
                </Button>
              </div>
            ) : listings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Map className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No properties found</h3>
                <p className="text-gray-500 max-w-sm">
                  Try adjusting your filters or expanding your search area
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={onClearFilters}
                >
                  Clear all filters
                </Button>
              </div>
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 gap-4'
                : 'space-y-4'
              }>
                {listingsWithDistance.map((listing, index) => (
                  <div
                    key={listing.id}
                    ref={(el) => (listingRefs.current[listing.id] = el)}
                    className={highlightedId === listing.id ? 'ring-2 ring-blue-500 rounded-xl' : ''}
                  >
                    <PropertyResultCard
                      listing={listing}
                      onHover={setHighlightedId}
                      showCompare={true}
                      isCompareSelected={compareIds.includes(listing.id)}
                      onCompareToggle={handleCompareToggle}
                      distance={listing.calculatedDistance}
                    />
                  </div>
                ))}
              </div>
            )}
            
            {/* Load more / pagination */}
            {listings.length > 0 && listings.length < totalCount && (
              <div className="flex justify-center mt-8">
                <Button variant="outline" size="lg" className="rounded-full px-8">
                  Load more properties
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
          
          {/* Context Panel - Desktop only */}
          <div className="hidden lg:block w-[416px] flex-shrink-0">
            <PropertyContextPanel
              listings={listings}
              searchCenter={searchCenter}
              highlightedListingId={highlightedId}
              onMarkerClick={handleMarkerClick}
              compareIds={compareIds}
              onCompare={() => setShowCompareModal(true)}
              onClearCompare={() => setCompareIds([])}
              hoverZoomSettings={hoverZoomSettings}
            />
          </div>
        </div>
      </div>
      
      {/* Mobile Map Sheet */}
      <Sheet open={showMobileMap} onOpenChange={setShowMobileMap}>
        <SheetContent side="bottom" className="h-[85vh] p-0">
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <Map className="w-5 h-5" />
              Map View
            </SheetTitle>
          </SheetHeader>
          <div className="h-[calc(100%-60px)]">
            <PropertyContextPanel
              listings={listings}
              searchCenter={searchCenter}
              highlightedListingId={highlightedId}
              onMarkerClick={(id) => {
                handleMarkerClick(id);
                setShowMobileMap(false);
              }}
              compareIds={compareIds}
              onCompare={() => {
                setShowMobileMap(false);
                setShowCompareModal(true);
              }}
              onClearCompare={() => setCompareIds([])}
              hoverZoomSettings={hoverZoomSettings}
            />
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Compare Modal */}
      <CompareProperties
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        selectedIds={compareIds}
        onRemove={(id) => setCompareIds(prev => prev.filter(i => i !== id))}
      />
      
      {/* Compare FAB - Mobile */}
      {compareIds.length > 0 && (
        <div className="fixed bottom-4 left-4 right-4 lg:hidden z-40">
          <div className="bg-white rounded-full shadow-lg border p-2 flex items-center justify-between">
            <div className="flex items-center gap-2 pl-2">
              <Badge className="bg-blue-600">{compareIds.length}</Badge>
              <span className="text-sm font-medium">Selected to compare</span>
            </div>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => setCompareIds([])}
              >
                Clear
              </Button>
              <Button 
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 rounded-full"
                onClick={() => setShowCompareModal(true)}
                disabled={compareIds.length < 2}
              >
                Compare
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
